import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { EntryList } from "@/components/Entries/EntryList";
import { EmptyState } from "@/components/Entries/EmptyState";
import { SharedNotesLoadingState } from "@/components/SharedNotes/LoadingState";
import { useEntryActions } from "@/hooks/useEntryActions";
import { Database } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

type Entry = Database["public"]["Tables"]["entries"]["Row"] & {
  creator_email?: string;
  last_editor_email?: string;
};

const Postscript = () => {
  const { user } = useAuth();
  const canCreate = user?.email === "jacobtart8@gmail.com";

  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  
  const {
    toggleFavorite,
    togglePinned,
    copyShareLink,
    stripHtmlTags
  } = useEntryActions();

  useEffect(() => {
    fetchPostscriptEntries();
  }, []);

  const fetchPostscriptEntries = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from("entries")
        .select(`
          *,
          profiles!entries_creator_id_fkey(full_name)
        `)
        .eq("is_postscript", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching postscript entries:", error);
        return;
      }

      const entriesWithEmails = data?.map(entry => ({
        ...entry,
        creator_email: entry.profiles?.full_name || "Unknown Author"
      })) || [];

      setEntries(entriesWithEmails);
    } catch (error) {
      console.error("Error in fetchPostscriptEntries:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <SharedNotesLoadingState />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-baseline gap-3 mb-6">
        <h1 className="text-3xl font-bold">Postscript</h1>
        <span className="text-lg text-muted-foreground">by Jacob</span>
        {canCreate && (
          <Button
            size="sm"
            className="ml-4"
            onClick={() =>
              window.location.pathname !== "/dashboard/entry/new"
                ? window.location.assign("/dashboard/entry/new?is_postscript=true")
                : undefined
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            New Postscript
          </Button>
        )}
      </div>
      
      <div className="mb-6">
        <p className="text-muted-foreground">
          Curated thoughts and insights shared with the community.
        </p>
      </div>

      {entries.length === 0 ? (
        <EmptyState hideButton customTitle="Postscript is empty right now" />
      ) : (
        <EntryList
          entries={entries}
          currentUserId={user?.id}
          toggleFavorite={toggleFavorite}
          togglePinned={togglePinned}
          copyShareLink={copyShareLink}
          stripHtmlTags={stripHtmlTags}
        />
      )}
    </div>
  );
};

export default Postscript;
